import {isNil} from 'ramda'
import typeCast, {type AskedType} from 'utils/typeCast'

import type {Brand} from '@vio/core/src/brand/types'
import type {GetServerSidePropsContext} from 'next'

type TypeMap = {
  string: string
  number: number
  array: unknown[]
  boolean: boolean
}

// typeCast will cast NaN to 0 by default, we want to reject them instead
const REJECT_NAN = true

/**
 * Generate a URL object from the URL
 */
export const getUrlObjectFromContext = (
  context: GetServerSidePropsContext,
  brand: Brand
): URL => {
  const {resolvedUrl} = context
  return new URL(resolvedUrl, brand.siteAddresses.address)
}

/**
 * Generate a plain object from URLSearchParams for compatibility with older utilities
 */
export const objectFromURLSearchParams = (
  searchParams: URLSearchParams
): Record<string, string | string[]> =>
  Object.fromEntries(searchParams.entries())

/**
 * Get a parameter and typecast it if needed
 * When isMultiValue is true, the return type is an array of the specified type (required)
 */
export const makeGetSearchParameter = (searchParameters: URLSearchParams) => {
  // Overloads ensure we get correct return types
  function getParameter(name: string): string | undefined
  function getParameter<T extends AskedType>(
    name: string,
    type: T,
    isMultiValue?: never
  ): TypeMap[T] | undefined
  function getParameter<T extends AskedType>(
    name: string,
    type: T,
    isMultiValue: true
  ): TypeMap[T][] | undefined
  function getParameter<T extends AskedType>(
    name: string,
    type?: T,
    isMultiValue?: boolean
  ) {
    if (isMultiValue) {
      const values = searchParameters.getAll(name)
      if (values.length === 0) return undefined
      return values.map(param => typeCast(param, type, REJECT_NAN))
    }

    const parameter = searchParameters.get(name)
    if (isNil(parameter)) return undefined
    return type ? typeCast(parameter, type, REJECT_NAN) : parameter
  }
  return getParameter
}
